import React, { useState, forwardRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Box, TextField, FormControl, InputLabel,
  Select, MenuItem, Chip, Tooltip, Button, ButtonGroup,
  FormControlLabel, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions, Slide, Divider
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import DeleteIcon from '@material-ui/icons/Delete';
import WarningIcon from '@material-ui/icons/Warning';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import Autocomplete from '@material-ui/lab/Autocomplete';
import HerbicideNotesModal from './sections/HerbicideNotesModal';
import {
  AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS, AREA_CATEGORIES,
  SERVICES, SERVICE_LABELS, getAreaTypeLabel, getAreaTypeColor,
  getSprayTypeForArea, SPRAY_TYPES, SPRAY_TYPE_LABELS, SPRAY_TYPE_COLORS,
  PLANT_BEARING_TYPES, SHRUB_DENSITY_OPTIONS, MODIFIER_DEFS,
  TREE_TYPES, TREE_CONDITION_OPTIONS, COMMON_TREE_SPECIES,
  resolveAreaType, getSuggestedServices,
  EXCLUSION_TYPES, EXCLUSION_TYPE_LABELS, EXCLUSION_TYPE_COLORS,
  isExclusionArea,
} from '../../SiteTakeoff/siteTakeoffConstants';


const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 16px',
    borderBottom: '1px solid #e0e0e0',
    background: '#fafafa',
  },
  title: {
    fontSize: 15,
    fontWeight: 500,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  geometryInfo: {
    padding: 12,
    background: '#f5f5f5',
    borderRadius: 6,
    fontSize: 12,
  },
  geometryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  sprayBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 10,
    borderRadius: 6,
    marginTop: 8,
  },
  serviceChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  serviceChip: {
    fontSize: 11,
    height: 24,
    cursor: 'pointer',
  },
  modifierRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    gap: 8,
  },
  modifierLabel: {
    fontSize: 12,
    color: '#555',
    flex: 1,
  },
  toggleGroup: {
    '& .MuiButton-root': {
      fontSize: 11,
      padding: '2px 8px',
      minWidth: 50,
      textTransform: 'none',
    },
  },
  densityRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  densityLabel: {
    fontSize: 12,
    color: '#555',
    minWidth: 90,
  },
  exclusionSection: {
    padding: 12,
    background: '#FFF8E1',
    borderRadius: 6,
    border: '1px solid #FFE082',
  },
  exclusionBreakdown: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '3px 0',
    fontSize: 12,
  },
  exclusionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    background: '#f5f5f5',
    borderRadius: 4,
    marginTop: 6,
    cursor: 'pointer',
    '&:hover': { background: '#eeeeee' },
  },
  exclusionDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  parentLabel: {
    fontSize: 12,
    padding: '6px 10px',
    background: '#E3F2FD',
    borderRadius: 4,
    color: '#1565C0',
    marginBottom: 8,
  },
}));

const formatSF = (num) => (parseFloat(num) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

const SlideUp = forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * AreaEditor — focused editing view for a single area, replaces sidebar content.
 * Full V1 field parity: name, type, geometry, plant density, modifiers,
 * services, pre-emergent, tree details, notes.
 */
const AreaEditor = ({
  area,
  areas = [],
  onClose,
  onUpdate,
  onDelete,
  onDuplicate,
  isEditingShape,
  onStartEditShape,
  onStopEditShape,
  onDrawExclusion,
  onEditArea,
  isDragging,
  onStopDrag,
}) => {
  const classes = useStyles();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [dupGroupName, setDupGroupName] = useState('');
  const [groupPickerOpen, setGroupPickerOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [herbicideModalOpen, setHerbicideModalOpen] = useState(false);

  if (!area) return null;

  const resolvedType = resolveAreaType(area.areaType);
  const isPlantBearing = PLANT_BEARING_TYPES.includes(resolvedType);
  const isTreeType = TREE_TYPES.includes(resolvedType);

  const isExclusion = isExclusionArea(area);
  const areaId = area._id || area.id;

  // For parent areas: find child exclusions
  const childExclusions = isExclusion ? [] : areas.filter(a => {
    const pid = a.parentAreaId;
    return pid && (pid === areaId || pid.toString?.() === areaId);
  });
  const hasExclusions = childExclusions.length > 0;
  const grossSF = parseFloat(area.area) || 0;
  const exclusionSF = childExclusions.reduce((sum, e) => sum + (parseFloat(e.area) || 0), 0);
  const netSF = grossSF - exclusionSF;

  // For exclusion areas: find parent name
  const parentArea = isExclusion ? areas.find(a => {
    const pid = area.parentAreaId;
    const aid = a._id || a.id;
    return aid === pid || aid === pid?.toString?.();
  }) : null;

  // Existing groups from all areas (for the group picker)
  const existingGroups = (() => {
    const map = {};
    areas.forEach(a => {
      if (a.groupId && a.groupName) {
        if (!map[a.groupId]) map[a.groupId] = { id: a.groupId, name: a.groupName, count: 0 };
        map[a.groupId].count++;
      }
    });
    return Object.values(map);
  })();

  const sprayType = getSprayTypeForArea(area.areaType);
  const hasPreEmergent = (area.servicesRequired || []).includes(SERVICES.PRE_EMERGENT);
  const canSpray = sprayType !== SPRAY_TYPES.NONE;

  const density = area.plantDensity || { treeCount: 0, shrubDensity: 'none', groundcoverDensity: 'none' };
  const modifiers = area.modifiers || {};
  const treeDetails = area.treeDetails || {};

  const handleTogglePreEmergent = () => {
    const current = area.servicesRequired || [];
    const next = hasPreEmergent
      ? current.filter(s => s !== SERVICES.PRE_EMERGENT)
      : [...current, SERVICES.PRE_EMERGENT];
    onUpdate({ servicesRequired: next });
  };

  const handleToggleService = (service) => {
    const current = area.servicesRequired || [];
    const next = current.includes(service)
      ? current.filter(s => s !== service)
      : [...current, service];
    onUpdate({ servicesRequired: next });
  };

  const updateDensity = (key, val) => onUpdate({ plantDensity: { [key]: val } });
  const updateModifier = (key, val) => onUpdate({ modifiers: { [key]: val } });
  const updateTreeDetail = (key, val) => onUpdate({ treeDetails: { [key]: val } });

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton size="small" onClick={onClose}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography className={classes.title}>
          {area.name || getAreaTypeLabel(area.areaType)}
        </Typography>
        {onStartEditShape && (
          <Tooltip title={isEditingShape ? 'Done editing (Esc)' : 'Edit shape'}>
            <IconButton
              size="small"
              onClick={isEditingShape ? onStopEditShape : onStartEditShape}
              style={{
                background: isEditingShape ? '#4caf50' : 'transparent',
                color: isEditingShape ? '#fff' : '#666',
              }}
            >
              {isEditingShape
                ? <CheckIcon fontSize="small" />
                : <EditIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
        {onDuplicate && !isExclusion && (
          <Tooltip title="Duplicate area">
            <IconButton size="small" onClick={() => {
              setDupGroupName(area.groupName || getAreaTypeLabel(area.areaType));
              setDuplicateOpen(true);
            }}>
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDuplicate && isExclusion && (
          <Tooltip title="Duplicate exclusion zone">
            <IconButton size="small" onClick={() => onDuplicate({ keepExclusion: true })}>
              <FileCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete area">
          <IconButton size="small" onClick={() => setDeleteConfirmOpen(true)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      {/* Banner when editing shape */}
      {isEditingShape && (
        <div style={{
          padding: '8px 16px',
          background: '#4caf50',
          color: '#fff',
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'center',
        }}>
          {area.geometryType === 'point'
            ? 'Drag the marker to move it'
            : area.geometryType === 'ellipse'
              ? 'Drag the center to move or edges to resize'
              : 'Drag vertices or drag the whole shape to move'}
          <span style={{ opacity: 0.85, marginLeft: 8 }}>— Press Esc or tap ✓ when done</span>
        </div>
      )}

      {/* Banner when in drag mode (after duplicate) */}
      {isDragging && !isEditingShape && (
        <div style={{
          padding: '8px 16px',
          background: '#1976d2',
          color: '#fff',
          fontSize: 12,
          fontWeight: 500,
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}>
          Drag this area to position it on the map
          <Button
            size="small"
            onClick={onStopDrag}
            style={{
              color: '#fff',
              background: 'rgba(255,255,255,0.2)',
              textTransform: 'none',
              fontSize: 11,
              minWidth: 0,
              padding: '2px 10px',
            }}
          >
            Done
          </Button>
        </div>
      )}

      <div className={classes.body}>
        {/* Name */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Name</Typography>
          <TextField
            fullWidth size="small" variant="outlined"
            value={area.name || ''}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="e.g. SE Turf"
          />
        </Box>

        {/* Group */}
        {!isExclusion && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Group</Typography>
            {area.groupId ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: '#EEF2FF',
                borderRadius: 6,
              }}>
                <span style={{
                  fontSize: 10,
                  color: '#6366F1',
                }}>
                  ▪
                </span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#4338CA', flex: 1 }}>
                  {area.groupName || 'Unnamed group'}
                </span>
                <Tooltip title="Remove from group">
                  <IconButton
                    size="small"
                    onClick={() => onUpdate({ groupId: null, groupName: null })}
                    style={{ padding: 2 }}
                  >
                    <span style={{ fontSize: 14, color: '#999' }}>✕</span>
                  </IconButton>
                </Tooltip>
              </div>
            ) : groupPickerOpen ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {existingGroups.length > 0 && existingGroups.map(g => (
                  <div
                    key={g.id}
                    onClick={() => {
                      onUpdate({ groupId: g.id, groupName: g.name });
                      setGroupPickerOpen(false);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      background: '#f5f5f5',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: '#6366F1', fontSize: 10 }}>▪</span>
                    <span style={{ flex: 1 }}>{g.name}</span>
                    <span style={{ fontSize: 11, color: '#999' }}>{g.count} areas</span>
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 6 }}>
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="New group name..."
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    style={{ flex: 1 }}
                    inputProps={{ style: { fontSize: 13, padding: '6px 10px' } }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newGroupName.trim()) {
                        onUpdate({ groupId: uuidv4(), groupName: newGroupName.trim() });
                        setNewGroupName('');
                        setGroupPickerOpen(false);
                      }
                    }}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disabled={!newGroupName.trim()}
                    onClick={() => {
                      onUpdate({ groupId: uuidv4(), groupName: newGroupName.trim() });
                      setNewGroupName('');
                      setGroupPickerOpen(false);
                    }}
                    style={{ textTransform: 'none', fontSize: 12, minWidth: 0, boxShadow: 'none' }}
                  >
                    Create
                  </Button>
                </div>
                <Button
                  size="small"
                  onClick={() => setGroupPickerOpen(false)}
                  style={{ textTransform: 'none', fontSize: 12, color: '#999' }}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => { setNewGroupName(''); setGroupPickerOpen(true); }}
                style={{ textTransform: 'none', fontSize: 12, color: '#6366F1', borderColor: '#C7D2FE' }}
              >
                Add to group
              </Button>
            )}
          </Box>
        )}

        {/* Area Type — hidden for exclusion areas (always DO_NOT_SERVICE) */}
        {!isExclusion && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Area Type</Typography>
          <FormControl fullWidth size="small" variant="outlined">
            <Select
              value={area.areaType || AREA_TYPES.GR_CLEAN}
              onChange={(e) => {
                const newType = e.target.value;
                onUpdate({
                  areaType: newType,
                  fillColor: getAreaTypeColor(newType),
                  servicesRequired: getSuggestedServices(newType),
                });
              }}
            >
              {Object.entries(AREA_CATEGORIES).map(([catKey, cat]) => [
                <MenuItem key={`header-${catKey}`} disabled style={{
                  opacity: 1, fontWeight: 600, fontSize: '0.72rem', background: '#f5f5f5',
                }}>
                  {cat.label}
                </MenuItem>,
                ...cat.types.map(t => (
                  <MenuItem key={t} value={t}>
                    <span style={{
                      width: 10, height: 10, borderRadius: '50%',
                      background: AREA_TYPE_COLORS[t],
                      display: 'inline-block', marginRight: 8,
                      border: '1px solid rgba(0,0,0,0.15)',
                    }} />
                    {AREA_TYPE_LABELS[t]}
                  </MenuItem>
                )),
              ])}
            </Select>
          </FormControl>

          {/* Pre-emergent pill */}
          <div
            className={classes.sprayBox}
            style={{
              background: canSpray ? '#fff8e1' : '#f5f5f5',
              border: `1px solid ${canSpray ? '#ffe082' : '#e0e0e0'}`,
            }}
          >
            <span style={{
              width: 10, height: 10, borderRadius: '50%',
              background: SPRAY_TYPE_COLORS[sprayType],
              border: '1px solid rgba(0,0,0,0.15)',
            }} />
            <span style={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
              Pre-Emergent: {SPRAY_TYPE_LABELS[sprayType]}
            </span>
            {canSpray && (
              <Chip
                size="small"
                label={hasPreEmergent ? 'Applied' : 'Skip'}
                onClick={handleTogglePreEmergent}
                style={{
                  background: hasPreEmergent ? '#4caf50' : '#e0e0e0',
                  color: hasPreEmergent ? '#fff' : '#666',
                  height: 22, fontSize: 11, cursor: 'pointer',
                }}
              />
            )}
            <Tooltip title="Herbicide notes">
              <IconButton
                size="small"
                onClick={() => setHerbicideModalOpen(true)}
                style={{
                  padding: 3,
                  opacity: (area.herbicideNotes || area.herbicidePhotos?.length) ? 1 : 0.4,
                }}
              >
                <span style={{ position: 'relative', display: 'inline-flex' }}>
                  <DescriptionOutlinedIcon style={{ fontSize: 16 }} />
                  {(area.herbicideNotes || area.herbicidePhotos?.length > 0) && (
                    <span style={{
                      position: 'absolute', top: -2, right: -2,
                      width: 6, height: 6, borderRadius: '50%',
                      background: '#e53935',
                    }} />
                  )}
                </span>
              </IconButton>
            </Tooltip>
          </div>
        </Box>
        )}

        {/* Geometry */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Geometry</Typography>
          <div className={classes.geometryInfo}>
            {area.geometryType === 'point' ? (
              <div className={classes.geometryRow}>
                <span>Location</span>
                <strong>
                  {area.paths?.[0]?.lat?.toFixed(5)}, {area.paths?.[0]?.lng?.toFixed(5)}
                </strong>
              </div>
            ) : area.geometryType === 'ellipse' ? (
              <>
                <div className={classes.geometryRow}>
                  <span>Radius</span>
                  <strong>{formatSF(area.radiusX || 0)} ft</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Area</span>
                  <strong>{formatSF(area.area)} SF</strong>
                </div>
              </>
            ) : area.geometryType === 'polyline' ? (
              <>
                <div className={classes.geometryRow}>
                  <span>Length</span>
                  <strong>{formatSF(area.length)} LF</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Width</span>
                  <strong>{area.width || 0} ft</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Area</span>
                  <strong>{formatSF(area.area)} SF</strong>
                </div>
              </>
            ) : (
              <>
                <div className={classes.geometryRow}>
                  <span>Area</span>
                  <strong>{formatSF(area.area)} SF</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Acres</span>
                  <strong>{parseFloat(area.areaAcres || 0).toFixed(3)}</strong>
                </div>
                <div className={classes.geometryRow}>
                  <span>Perimeter</span>
                  <strong>{formatSF(area.perimeter)} LF</strong>
                </div>
              </>
            )}
          </div>
        </Box>

        {/* Polyline width editor */}
        {area.geometryType === 'polyline' && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Width (ft)</Typography>
            <TextField
              fullWidth size="small" variant="outlined" type="number"
              value={area.width || ''}
              onChange={(e) => {
                const width = parseFloat(e.target.value) || 0;
                const length = parseFloat(area.length) || 0;
                const newArea = (length * width).toFixed(2);
                onUpdate({
                  width,
                  area: newArea,
                  areaAcres: (length * width / 43560).toFixed(4),
                });
              }}
              inputProps={{ min: 0, step: 0.5 }}
            />
          </Box>
        )}

        {/* Plant density — only for plant-bearing types */}
        {isPlantBearing && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Plant Density</Typography>
            <div className={classes.densityRow}>
              <span className={classes.densityLabel}>Trees</span>
              <TextField
                size="small" variant="outlined" type="number"
                value={density.treeCount || 0}
                onChange={(e) => updateDensity('treeCount', parseInt(e.target.value, 10) || 0)}
                inputProps={{ min: 0 }}
                style={{ width: 80 }}
              />
            </div>
            <div className={classes.densityRow}>
              <span className={classes.densityLabel}>Shrub density</span>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <Select
                  value={density.shrubDensity || 'none'}
                  onChange={(e) => updateDensity('shrubDensity', e.target.value)}
                >
                  {SHRUB_DENSITY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className={classes.densityRow}>
              <span className={classes.densityLabel}>Groundcover</span>
              <FormControl size="small" variant="outlined" style={{ flex: 1 }}>
                <Select
                  value={density.groundcoverDensity || 'none'}
                  onChange={(e) => updateDensity('groundcoverDensity', e.target.value)}
                >
                  {SHRUB_DENSITY_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </Box>
        )}

        {/* Tree details — only for tree disposition types */}
        {isTreeType && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Tree Details</Typography>
            <Autocomplete
              freeSolo
              options={COMMON_TREE_SPECIES}
              value={treeDetails.species || ''}
              onChange={(e, val) => updateTreeDetail('species', val || '')}
              onInputChange={(e, val) => updateTreeDetail('species', val || '')}
              renderInput={(params) => (
                <TextField {...params} size="small" variant="outlined" placeholder="Species" />
              )}
              size="small"
              style={{ marginBottom: 8 }}
            />
            <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap={8} style={{ marginBottom: 8 }}>
              <TextField
                size="small" variant="outlined" type="number"
                label="Caliper (in)"
                value={treeDetails.caliper || ''}
                onChange={(e) => updateTreeDetail('caliper', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <TextField
                size="small" variant="outlined" type="number"
                label="Height (ft)"
                value={treeDetails.height || ''}
                onChange={(e) => updateTreeDetail('height', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <TextField
                size="small" variant="outlined" type="number"
                label="Spread (ft)"
                value={treeDetails.canopySpread || ''}
                onChange={(e) => updateTreeDetail('canopySpread', parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
              />
              <FormControl size="small" variant="outlined">
                <InputLabel>Condition</InputLabel>
                <Select
                  label="Condition"
                  value={treeDetails.condition || ''}
                  onChange={(e) => updateTreeDetail('condition', e.target.value)}
                >
                  {TREE_CONDITION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}

        {/* Modifiers */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Modifiers</Typography>
          {Object.entries(MODIFIER_DEFS).map(([key, def]) => {
            const currentVal = modifiers[key] ?? def.default;
            if (def.type === 'toggle') {
              return (
                <div key={key} className={classes.modifierRow}>
                  <span className={classes.modifierLabel}>{def.label}</span>
                  <ButtonGroup size="small" className={classes.toggleGroup}>
                    {def.options.map(opt => (
                      <Button
                        key={opt}
                        variant={currentVal === opt ? 'contained' : 'outlined'}
                        color={currentVal === opt ? 'primary' : 'default'}
                        onClick={() => updateModifier(key, opt)}
                      >
                        {opt}
                      </Button>
                    ))}
                  </ButtonGroup>
                </div>
              );
            }
            if (def.type === 'select') {
              return (
                <div key={key} className={classes.modifierRow}>
                  <span className={classes.modifierLabel}>{def.label}</span>
                  <FormControl size="small" variant="outlined" style={{ minWidth: 110 }}>
                    <Select
                      value={currentVal}
                      onChange={(e) => updateModifier(key, e.target.value)}
                    >
                      {def.options.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              );
            }
            if (def.type === 'checkbox') {
              return (
                <div key={key} className={classes.modifierRow}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!!currentVal}
                        onChange={(e) => updateModifier(key, e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<span className={classes.modifierLabel}>{def.label}</span>}
                  />
                </div>
              );
            }
            return null;
          })}
        </Box>

        {/* Exclusion — parent info (for exclusion areas) */}
        {isExclusion && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Exclusion Zone</Typography>
            <div className={classes.parentLabel}>
              Subtracts from: {parentArea ? (parentArea.name || getAreaTypeLabel(parentArea.areaType)) : 'Parent area'}
            </div>
            <FormControl fullWidth size="small" variant="outlined">
              <InputLabel>Exclusion Type</InputLabel>
              <Select
                label="Exclusion Type"
                value={area.exclusionType || 'other'}
                onChange={(e) => onUpdate({ exclusionType: e.target.value })}
              >
                {Object.entries(EXCLUSION_TYPE_LABELS).map(([val, label]) => (
                  <MenuItem key={val} value={val}>
                    <span style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: EXCLUSION_TYPE_COLORS[val],
                      display: 'inline-block',
                      marginRight: 8,
                      border: '1px solid rgba(0,0,0,0.15)',
                    }} />
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Exclusion zones (for parent areas) */}
        {!isExclusion && (
          <Box className={classes.section}>
            <Typography className={classes.sectionTitle}>Exclusion Zones</Typography>
            {hasExclusions ? (
              <div className={classes.exclusionSection}>
                <div className={classes.exclusionBreakdown}>
                  <span>Gross area</span>
                  <strong>{formatSF(grossSF)} SF</strong>
                </div>
                <div className={classes.exclusionBreakdown}>
                  <span>Exclusions ({childExclusions.length})</span>
                  <strong style={{ color: '#e53935' }}>−{formatSF(exclusionSF)} SF</strong>
                </div>
                <Divider style={{ margin: '4px 0' }} />
                <div className={classes.exclusionBreakdown}>
                  <span style={{ fontWeight: 600 }}>Net area</span>
                  <strong style={{ color: '#2e7d32' }}>{formatSF(netSF)} SF</strong>
                </div>
                {childExclusions.map(exc => {
                  const excId = exc._id || exc.id;
                  const excLabel = EXCLUSION_TYPE_LABELS[exc.exclusionType] || 'Other';
                  const excColor = EXCLUSION_TYPE_COLORS[exc.exclusionType] || '#9E9E9E';
                  return (
                    <div
                      key={excId}
                      className={classes.exclusionItem}
                      onClick={() => onEditArea && onEditArea(excId)}
                    >
                      <div className={classes.exclusionDot} style={{ background: excColor }} />
                      <span style={{ flex: 1, fontSize: 12 }}>
                        {exc.name || excLabel}
                      </span>
                      <span style={{ fontSize: 11, color: '#999' }}>
                        {formatSF(exc.area)} SF
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 8 }}>
                No exclusion zones. Draw interior features (buildings, pools) to subtract from this area.
              </Typography>
            )}
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => onDrawExclusion && onDrawExclusion(areaId)}
              style={{ marginTop: 8, textTransform: 'none', fontSize: 12 }}
            >
              Draw exclusion zone
            </Button>
          </Box>
        )}

        {/* Services — hide for exclusion areas (they don't generate labor) */}
        {!isExclusion && (
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Services</Typography>
          <div className={classes.serviceChips}>
            {Object.entries(SERVICES).map(([key, svcValue]) => {
              const isActive = (area.servicesRequired || []).includes(svcValue);
              return (
                <Chip
                  key={svcValue}
                  label={SERVICE_LABELS[svcValue]}
                  size="small"
                  className={classes.serviceChip}
                  onClick={() => handleToggleService(svcValue)}
                  variant={isActive ? 'default' : 'outlined'}
                  style={{
                    background: isActive ? '#1976d2' : 'transparent',
                    color: isActive ? '#fff' : '#666',
                    borderColor: isActive ? '#1976d2' : '#ccc',
                  }}
                />
              );
            })}
          </div>
        </Box>
        )}

        {/* Notes */}
        <Box className={classes.section}>
          <Typography className={classes.sectionTitle}>Notes</Typography>
          <TextField
            fullWidth size="small" variant="outlined"
            multiline minRows={2}
            value={area.notes || ''}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Optional notes..."
          />
        </Box>
      </div>

      {/* Duplicate + group dialog */}
      <Dialog
        open={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        TransitionComponent={SlideUp}
        PaperProps={{
          style: { borderRadius: 12, minWidth: 320, overflow: 'hidden' },
        }}
      >
        <div style={{
          padding: '24px 24px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: '#E3F2FD',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}>
            <FileCopyIcon style={{ color: '#1976d2', fontSize: 24 }} />
          </div>
          <Typography style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 4 }}>
            Duplicate Area
          </Typography>
          <Typography style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', marginBottom: 16 }}>
            A copy will be placed nearby. Add to a group to keep related areas together.
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Group name (optional)"
            value={dupGroupName}
            onChange={(e) => setDupGroupName(e.target.value)}
            placeholder="e.g. Parking Islands"
            style={{ marginBottom: 8 }}
          />
        </div>
        <DialogActions style={{ padding: '16px 24px', gap: 8 }}>
          <Button
            onClick={() => {
              setDuplicateOpen(false);
              onDuplicate();
            }}
            style={{ textTransform: 'none', color: '#6B7280', fontWeight: 500, flex: 1 }}
          >
            Skip group
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setDuplicateOpen(false);
              const gid = area.groupId || uuidv4();
              onDuplicate({ groupId: gid, groupName: dupGroupName.trim() || null });
            }}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              flex: 1,
              borderRadius: 8,
              boxShadow: 'none',
            }}
          >
            Duplicate & Group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        TransitionComponent={SlideUp}
        PaperProps={{
          style: {
            borderRadius: 12,
            minWidth: 320,
            overflow: 'hidden',
          },
        }}
      >
        <div style={{
          padding: '24px 24px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <div style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: '#FEE2E2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}>
            <DeleteIcon style={{ color: '#DC2626', fontSize: 24 }} />
          </div>
          <Typography style={{ fontSize: 16, fontWeight: 600, color: '#1F2937', marginBottom: 8 }}>
            Delete {isExclusion ? 'exclusion zone' : 'area'}?
          </Typography>
          <Typography style={{ fontSize: 13, color: '#6B7280', textAlign: 'center', lineHeight: 1.5 }}>
            <strong>{area.name || getAreaTypeLabel(area.areaType)}</strong>
            {!isExclusion && grossSF > 0 && <> &middot; {formatSF(grossSF)} SF</>}
          </Typography>
          {hasExclusions && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginTop: 12,
              padding: '8px 12px',
              background: '#FEF3C7',
              borderRadius: 6,
              fontSize: 12,
              color: '#92400E',
            }}>
              <WarningIcon style={{ fontSize: 16 }} />
              {childExclusions.length} exclusion zone{childExclusions.length > 1 ? 's' : ''} will also be deleted
            </div>
          )}
        </div>
        <DialogActions style={{ padding: '20px 24px', gap: 8 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            style={{ textTransform: 'none', color: '#6B7280', fontWeight: 500, flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => { setDeleteConfirmOpen(false); onDelete(); }}
            style={{
              textTransform: 'none',
              background: '#DC2626',
              color: '#fff',
              fontWeight: 600,
              flex: 1,
              borderRadius: 8,
              boxShadow: 'none',
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Herbicide notes modal */}
      <HerbicideNotesModal
        open={herbicideModalOpen}
        onClose={() => setHerbicideModalOpen(false)}
        herbicideNotes={area.herbicideNotes}
        herbicidePhotos={area.herbicidePhotos}
        areaId={areaId}
        onSave={(data) => onUpdate(data)}
      />
    </div>
  );
};

export default AreaEditor;
